const MAORI_LAND_BLOCK_LAYER_NAME = 'Māori Land Blocks (zoomed in only)'
const MAORI_LAND_BLOCK_LAYER_MIN_ZOOM = 9

const Map = (function () {
  let landBlocksData = null

  /**
   * Create and configure the the Leaflet map instance
   */
  const initMap = () => {
    // Create the map instance
    // The `mapConfig` config object is set in PHP. See `MapHelper->setMapConfigInJS` or `MapPageController` for reference.
    const map = createMap(mapConfig.options)
    // Setting the prefix to an empty string to remove the Ukraine flag
    map.attributionControl.setPrefix('')

    // Configure the basemap layers
    const basemapLayers = constructBasemapLayers(mapConfig.basemapLayers)
    basemapLayers[mapConfig.defaultBasemapLayerName].addTo(map)

    // Configure the overlay layers
    const overlayLayers = constructOverlayLayers()

    // Add markers and fit the map view so all the markers are visible by default
    const { markers, selectedMarker } = createMarkerCluster({
      markerConfig: mapConfig.markerConfig,
      maxClusteringZoomLevel: mapConfig.maxClusteringZoomLevel,
      readonly: mapConfig.readonly,
      selectedMarkerLatLng: mapConfig.selectedMarkerLatLng,
    })
    markers.addTo(map)

    if (selectedMarker) {
      // Center the map on this marker and open the pop up by default
      map.setView(selectedMarker.getLatLng(), mapConfig.maxFitBoundsZoom)
      selectedMarker.openPopup()
    } else if (mapConfig.markerConfig.length > 0) {
      map.fitBounds(markers?.getBounds(), { maxZoom: mapConfig.maxFitBoundsZoom, padding: [50, 50] })
    } else {
      // If no search results map shows whole of NZ, centered.
      map.setView([-40.9006, 174.886], 6)
    }

    if (mapConfig.readonly) {
      // If map is readonly disable map.sleep which allows scrolls once map is focused.
      map.sleep._stopWaiting()
    } else {
      // Add zoom control (controls on bottom of page so not blocked by search bar on mobile)
      L.control.zoom({ position: 'bottomleft' }).addTo(map)

      if (mapConfig.hasLayerControl) {
        // Add layer controls
        L.control.layers(basemapLayers, overlayLayers, { position: 'bottomright', hideSingleBase: true }).addTo(map)
      }

      map.on('overlayadd', async (e) => {
        if (e.layer === overlayLayers[MAORI_LAND_BLOCK_LAYER_NAME] && !landBlocksData) {
          const result = await window.fetch('/land-blocks.json')
          landBlocksData = await result.json()
          if (map.getZoom() > MAORI_LAND_BLOCK_LAYER_MIN_ZOOM) {
            e.layer.addData(landBlocksData)
          }
        }
      })
      map.on('zoomend', () => {
        if (overlayLayers[MAORI_LAND_BLOCK_LAYER_NAME] && landBlocksData) {
          if (map.getZoom() > MAORI_LAND_BLOCK_LAYER_MIN_ZOOM) {
            if (!overlayLayers[MAORI_LAND_BLOCK_LAYER_NAME].getLayers().length) {
              overlayLayers[MAORI_LAND_BLOCK_LAYER_NAME].addData(landBlocksData)
            }
          } else {
            overlayLayers[MAORI_LAND_BLOCK_LAYER_NAME].clearLayers()
          }
        }
      })
    }
  }

  /**
   *
   * @param {{
   *   markerConfig: MarkerConfig[],
   *   maxClusteringZoomLevel: number,
   *   readonly: boolean,
   *   selectedMarkerLatLng: string
   * }} input
   * @returns
   */
  const createMarkerCluster = ({ markerConfig, maxClusteringZoomLevel, readonly, selectedMarkerLatLng }) => {
    const markers = new L.markerClusterGroup({
      // Hide polygon that displays bounds of the cluster on hover
      showCoverageOnHover: false,
      maxClusterRadius: (zoom) => {
        return zoom <= maxClusteringZoomLevel ? 50 : 0
      },
      iconCreateFunction: clusterIcon,
    })

    let selectedMarker

    markerConfig.forEach((config) => {
      const marker = createMarker(config)
      const content = popupContent(config?.content)
      if (!readonly) {
        marker.bindPopup(content, { className: 'c-leaflet-popup', minWidth: 280 })

        if (selectedMarkerLatLng) {
          const latlng = selectedMarkerLatLng.split(',')
          const hLat = parseFloat(latlng[0])
          const hLng = parseFloat(latlng[1])
          const { lat: mLat, lng: mLng } = marker.getLatLng()

          // if the current marker LatLngs match the selected marker LatLngs set it as the selectedMarker
          if (!selectedMarker && mLat == hLat && mLng == hLng) {
            selectedMarker = marker
          }
        }
      }
      markers.addLayer(marker)
    })
    return { markers, selectedMarker }
  }

  /**
   * Create the Leaflet map instance using the provided options
   * @param {MapOptions} mapOptions
   * @returns
   */
  const createMap = (mapOptions) => {
    return L.map('map', {
      ...mapOptions,
      center: L.latLng(mapOptions.center),
    })
  }

  /**
   * Create marker based on the provided config
   * @param {MarkerConfig} markerConfig
   * @returns
   */
  const createMarker = (markerConfig) => {
    const marker = L.marker(new L.LatLng(markerConfig.latitude, markerConfig.longitude), {
      icon: L.icon({
        iconUrl: markerConfig.icon.url,
        iconSize: markerConfig.icon.iconSize, // size of the icon
        iconAnchor: markerConfig.icon.iconAnchor, // point of the icon which will correspond to marker's location
        popupAnchor: markerConfig.icon.popupAnchor, // point from which the popup should open relative to the iconAnchor
      }),
      title: markerConfig.title,
    })
    return marker
  }

  /**
   * Create tile layers for the base map using the provided layer config
   * @param {BaseMapConfig[]} layers
   * @returns
   */
  const constructBasemapLayers = (layers) => {
    return layers.reduce((result, layer) => {
      const tileLayer = L.tileLayer(layer.url, {
        ...layer,
      })
      return {
        ...result,
        [layer.name]: tileLayer,
      }
    }, {})
  }

  /**
   * Create geojson overlay layers using the provided layer config
   * @param {BaseMapConfig[]} layers
   * @returns
   */
  const constructOverlayLayers = () => {
    // Adds an empty feature collection and only load the data when they turn the layer on (see map.on('overlayadd'))
    return {
      [MAORI_LAND_BLOCK_LAYER_NAME]: L.geoJSON(
        {
          type: 'FeatureCollection',
          features: [],
        },
        {
          style: function (feature) {
            return {
              fillColor: '#9f2d23',
              fillOpacity: 0.25,
              color: '#9f2d23',
              opacity: 0.4,
            }
          },
        },
      ).bindPopup(
        function (layer) {
          return `
          <div class="c-map-popup c-map-popup--land-block">
            <div class="c-map-popup__content_container">
              <h2 class="c-map-popup__title">${layer.feature.properties.Mgmt_NAME}</h2>
              <dl>
                <div class="c-map-popup__data-row"><dt>Block Id</dt><dd>${layer.feature.properties.BLOCK_ID}</dd></div>
                <div class="c-map-popup__data-row"><dt>District</dt><dd>${layer.feature.properties.DISTRICT}</dd></div>
                <div class="c-map-popup__data-row"><dt>Area (ha)</dt><dd>${layer.feature.properties.BLOCK_AREA}</dd></div>
                <div class="c-map-popup__data-row"><dt>Land Status</dt><dd>${layer.feature.properties.LAND_STATU}</dd></div>
                <div class="c-map-popup__data-row"><dt>Plan</dt><dd>${layer.feature.properties.PLAN_REF}</dd></div>
                <div class="c-map-popup__data-row"><dt>Total shares</dt><dd>${layer.feature.properties.TOTAL_SHAR}</dd></div>
                <div class="c-map-popup__data-row"><dt>Total owners</dt><dd>${layer.feature.properties.TOTAL_OWNE}</dd></div>
                <div class="c-map-popup__disclaimer-row">
                  Note this land block outline may not be an accurate representation. For more detailed boundaries please refer to
                  <a href="https://www.maorilandonline.govt.nz/" target="_blank">the Maori Land Court site - Pataka Whenua</a>.
                </div>
              </dl>
            </div>
          </div>
        `
        },
        { className: 'c-leaflet-popup', minWidth: 280 },
      ),
    }
  }

  /**
   * Create the content for the marker popup
   * @param {PopupContent} popupContent
   * @returns {string}
   */
  const popupContent = ({ title, maraeLink, imageSrc, hapus, district }) => {
    const image = `<img class="c-map-popup__image" src=${imageSrc} alt="" />`
    const districts = district ? `<div class="c-map-popup__details-right">${district}</div>` : ''

    const popupTitle = maraeLink
      ? `<h2 class="c-map-popup__title"><a href="${maraeLink}"  class="c-map-popup__title-link">${title}</a></h2>`
      : `<h2 class="c-map-popup__title">${title}</h2>`

    const hapusTemplate =
      hapus.length > 0
        ? `<ul class="c-labelled-list"><li class="c-labelled-list__item"><h3 class="c-labelled-list__label c-labelled-list__label--dark" lang="mi">Hapū</h3><div class="c-labelled-list__value">${hapus}</div></li></ul>`
        : ''

    return `<div class="c-map-popup"><div class="c-map-popup__image-container">${image}</div><div class="c-map-popup__content_container">${popupTitle}<div class="c-map-popup__details"><div class="c-map-popup__details-left">${hapusTemplate}</div>${districts}
    </div></div></div>`
  }

  const clusterIcon = (cluster) => {
    const childMarkers = cluster.getAllChildMarkers()
    let count = Array.isArray(childMarkers) ? childMarkers.length : 0

    const containerClasses = ['c-cluster-icon']
    let width
    // As the count gets more digits update the styling class to increase the width.
    if (count < 10) {
      containerClasses.push('c-cluster-icon--small')
      width = 80
    } else if (count < 100) {
      containerClasses.push('c-cluster-icon--medium')
      width = 90
    } else if (count < 1000) {
      containerClasses.push('c-cluster-icon--large')
      width = 100
    } else if (count < 10000) {
      containerClasses.push('c-cluster-icon--xlarge')
      width = 110
    } else {
      containerClasses.push('c-cluster-icon--xxlarge')
      width = 120
    }
    return L.divIcon({
      className: containerClasses.join(' '),
      html: `<span class="c-cluster-icon__count">${count} Marae</span>`,
      iconSize: new L.Point(width, 24),
    })
  }

  return {
    init: () => {
      const mapContainer = document.querySelector('#map')
      if (mapContainer) {
        initMap()
      }
    },
  }
})()

export default Map
